import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/auth';
import DashboardLayout from './components/layout/DashboardLayout';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CollectionView from './pages/CollectionView';
import Start from './pages/Start';
import RoleSelect from './pages/RoleSelect';
import BuyerHome from './pages/BuyerHome';
import SellerCatalog from './pages/SellerCatalog';
import CustomerConnect from './pages/CustomerConnect';
import TeamLogin from './pages/TeamLogin';
import Pricing from './pages/Pricing';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import BulkUpload from './pages/BulkUpload';
import Categories from './pages/Categories';
import Broadcast from './pages/Broadcast';
import SalesBrain from './pages/SalesBrain';
import SellerOpportunities from './pages/SellerOpportunities';
import SellerFollowups from './pages/SellerFollowups';
import WhatsApp from './pages/WhatsApp';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Payments from './pages/Payments';
import Shipping from './pages/Shipping';
import Inventory from './pages/Inventory';
import { SHOW_STOCK_INVENTORY_UI } from './components/layout/Sidebar';
import AIAssistant from './pages/AIAssistant';
import Customers from './pages/Customers';
import Support from './pages/Support';
import Analytics from './pages/Analytics';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import AdminPendingNumbers from './pages/AdminPendingNumbers';
import AdminLayout from './pages/admin/AdminLayout';
import MasterSalesBrain from './pages/admin/MasterSalesBrain';
import AdminMatrix from './pages/admin/AdminMatrix';
import AdminSellerMatrix from './pages/admin/AdminSellerMatrix';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminVendors from './pages/admin/AdminVendors';
import AdminVendorDetail from './pages/admin/AdminVendorDetail';
import AdminAuditLogs from './pages/admin/AdminAuditLogs';
import {
  AdminConversations, AdminProducts, AdminCollections,
  AdminOrders, AdminPayments, AdminShipping, AdminReviews,
  AdminSubscriptions, AdminCoupons, AdminNotifications, AdminWhatsApp,
  AdminAIActivity, AdminSystemHealth, AdminSettings,
} from './pages/admin/AdminPages';
import AdminSellerPromotions from './pages/admin/AdminSellerPromotions';
import AdminBuyersPage from './pages/admin/AdminBuyersPage';
import AdminPlatformHealth from './pages/admin/AdminPlatformHealth';
import AdminGuestSellers from './pages/admin/AdminGuestSellers';
import AdminErrorCentre from './pages/admin/AdminErrorCentre';
import SellerViewCommandCenter from './pages/admin/SellerViewCommandCenter';
import SellerImpersonateBootstrap from './pages/admin/SellerImpersonateBootstrap';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import ContactUs from './pages/legal/ContactUs';
import DataDeletion from './pages/legal/DataDeletion';
import AdminAffiliate from './pages/admin/AdminAffiliate';
import MarketerLogin from './pages/affiliate/MarketerLogin';
import MarketerRegister from './pages/affiliate/MarketerRegister';
import MarketerDashboard from './pages/affiliate/MarketerDashboard';
import './App.css';

function Protected({ children }) {
  const { seller, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-fab-text-muted">Loading...</div>;
  if (!seller) return <Navigate to="/login" replace />;
  return children;
}

// Feb 2026 — Team-member department gate. If the currently-authenticated
// identity is a team member, block them from any department page not in
// their assigned list. Backend also enforces this via _enforce_team_member_path
// so URL manipulation cannot bypass it — the gate here is purely UX so
// they don't see a "403" toast on every navigation attempt.
const _DEPT_FOR_ROUTE = {
  '':            'Dashboard',
  'products':    'Products',
  'categories':  'Categories',
  'broadcast':   'Catalog Broadcast',
  'whatsapp':    'WhatsApp',
  'orders':      'Orders',
  'customers':   'Customers',
  'support':     'Customer Support',
  'ai':          'AI Assistant',
  'payments':    'Payments',
  'shipping':    'Shipping',
  'analytics':   'Reports & Analytics',
  'notifications': 'Notifications',
  'settings':    'Settings',
};

function DeptGate({ children }) {
  const { teamMember } = useAuth();
  const location = window.location.pathname;
  if (!teamMember) return children; // seller/admin — no gate
  const allowed = new Set(teamMember.departments || []);
  // Extract the first path segment after /dashboard/
  const m = location.match(/^\/dashboard\/?([^/]*)/);
  const seg = (m ? (m[1] || '') : '').toLowerCase();
  const dept = _DEPT_FOR_ROUTE[seg];
  if (!dept) return children; // unknown segment (e.g. legacy sub-page) — let backend enforce
  if (!allowed.has(dept)) {
    // Redirect to first allowed dept (prefer Orders if assigned).
    const firstDeptRoute = allowed.has('Orders') ? '/dashboard/orders'
      : (teamMember.departments || []).map((d) => ({
          'Dashboard': '/dashboard',
          'Products': '/dashboard/products',
          'Categories': '/dashboard/categories',
          'Catalog Broadcast': '/dashboard/broadcast',
          'WhatsApp': '/dashboard/whatsapp',
          'Orders': '/dashboard/orders',
          'Customers': '/dashboard/customers',
          'Customer Support': '/dashboard/support',
          'AI Assistant': '/dashboard/ai',
          'Payments': '/dashboard/payments',
          'Shipping': '/dashboard/shipping',
          'Reports & Analytics': '/dashboard/analytics',
          'Notifications': '/dashboard/notifications',
          'Settings': '/dashboard/settings',
        }[d]))[0] || '/dashboard';
    return <Navigate to={firstDeptRoute} replace />;
  }
  return children;
}

// Sales Brain is admin-only. Regular sellers are redirected away from the
// route (backend also enforces 403 on every /api/sales-brain/* call).
// Admin impersonation tabs carry the per-tab token so per-seller analysis works.
function SalesBrainGate({ children }) {
  const { seller, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-fab-text-muted">Loading...</div>;
  const isImp = typeof window !== 'undefined' && !!sessionStorage.getItem('fab_imp_token');
  if (!(seller?.is_admin || isImp)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          {/* Ticket 275862: a plain browser visit to the root runs the same
              entry logic as the installed app (/start) — new visitors get the
              Buyer/Seller screen, returning users keep their routing. The
              marketing page lives at /home. */}
          <Route path="/" element={<Start />} />
          <Route path="/home" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/c/:id" element={<CollectionView />} />
          <Route path="/start" element={<Start />} />
          <Route path="/welcome" element={<RoleSelect />} />
          <Route path="/me" element={<BuyerHome />} />
          <Route path="/s/:sid" element={<SellerCatalog />} />
          <Route path="/connect/:slug" element={<CustomerConnect />} />
          <Route path="/team-login" element={<TeamLogin />} />
          <Route path="/admin/seller-view/:vid" element={<Protected><SellerImpersonateBootstrap /></Protected>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/affiliate" element={<MarketerLogin />} />
          <Route path="/affiliate/register" element={<MarketerRegister />} />
          <Route path="/affiliate/join/:token" element={<MarketerRegister />} />
          <Route path="/affiliate/dashboard" element={<MarketerDashboard />} />
          <Route path="/dashboard" element={<Protected><DashboardLayout /></Protected>}>
            <Route index element={<DeptGate><Dashboard /></DeptGate>} />
            <Route path="products" element={<DeptGate><Products /></DeptGate>} />
            <Route path="products/upload" element={<DeptGate><BulkUpload /></DeptGate>} />
            <Route path="categories" element={<DeptGate><Categories /></DeptGate>} />
            <Route path="broadcast" element={<DeptGate><Broadcast /></DeptGate>} />
            <Route path="whatsapp" element={<DeptGate><WhatsApp /></DeptGate>} />
            <Route path="orders" element={<DeptGate><Orders /></DeptGate>} />
            <Route path="orders/:id" element={<DeptGate><OrderDetail /></DeptGate>} />
            <Route path="payments" element={<DeptGate><Payments /></DeptGate>} />
            <Route path="shipping" element={<DeptGate><Shipping /></DeptGate>} />
            <Route path="inventory" element={SHOW_STOCK_INVENTORY_UI ? <Inventory /> : <Navigate to="/dashboard" replace />} />
            <Route path="ai" element={<DeptGate><AIAssistant /></DeptGate>} />
            <Route path="opportunities" element={<DeptGate><SellerOpportunities /></DeptGate>} />
            <Route path="followups" element={<DeptGate><SellerFollowups /></DeptGate>} />
            <Route path="sales-brain" element={<SalesBrainGate><SalesBrain /></SalesBrainGate>} />
            <Route path="customers" element={<DeptGate><Customers /></DeptGate>} />
            <Route path="support" element={<DeptGate><Support /></DeptGate>} />
            <Route path="analytics" element={<DeptGate><Analytics /></DeptGate>} />
            <Route path="notifications" element={<DeptGate><Notifications /></DeptGate>} />
            <Route path="settings" element={<DeptGate><Settings /></DeptGate>} />
            <Route path="admin/numbers" element={<AdminPendingNumbers />} />
            <Route path="admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="sellers" element={<AdminVendors />} />
              <Route path="sellers/:vid" element={<AdminVendorDetail />} />
              <Route path="sellers/:vid/matrix" element={<AdminSellerMatrix />} />
              <Route path="vendors/:vid/matrix" element={<AdminSellerMatrix />} />
              <Route path="sellers/:id/view" element={<SellerViewCommandCenter />} />
              <Route path="vendors" element={<AdminVendors />} />
              <Route path="vendors/:vid" element={<AdminVendorDetail />} />
              <Route path="vendors/:id/view" element={<SellerViewCommandCenter />} />
              <Route path="buyers" element={<AdminBuyersPage />} />
              <Route path="guest-sellers" element={<AdminGuestSellers />} />
              <Route path="errors" element={<AdminErrorCentre />} />
              <Route path="conversations" element={<AdminConversations />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="collections" element={<AdminCollections />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="shipping" element={<AdminShipping />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="promotions" element={<AdminSellerPromotions />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="whatsapp" element={<AdminWhatsApp />} />
              <Route path="ai" element={<AdminAIActivity />} />
              <Route path="health" element={<AdminPlatformHealth />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="activity" element={<AdminAuditLogs />} />
              <Route path="master-sales-brain" element={<MasterSalesBrain />} />
              <Route path="matrix" element={<AdminMatrix />} />
              <Route path="affiliate/professional" element={<AdminAffiliate type="PROFESSIONAL" />} />
              <Route path="affiliate/community" element={<AdminAffiliate type="COMMUNITY" />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
